import React, {
  ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useStatusStore } from "@wss/error-tracking/use-status";
import { Icon } from "@clarkinc/zest-design-system";
import { logException } from "@wss/error-tracking";
import {
  CropBox,
  getBlobFromImageURL,
  handleLoadImageFromSrc,
  resizeImage,
} from "@wss/image-capture/utilities";
import { Image as WSSImage } from "@wss/image";
import bowser from "bowser";
import { toError } from "@wss/error-tracking/utils";
import { requestImageSearch } from "./requests";
import ImageSelectCropper, { Dimensions } from "./ImageSelectCropper";

const cancelDelayMs = 300;

const sharedButtonStyles = "border-0 bg-transparent p-0";

const sharedTextStyles =
  "flex justify-center mb-0 rounded-lg self-center text-center text-lg text-white";

function calculateViewSize(
  windowSize: Dimensions,
  heightPercent: number,
): Dimensions {
  // The actual box we're drawing must be a square that fits within the full width of the window, but only a percentage of the height
  const maxHeight = windowSize.height * (heightPercent / 100);
  const maxWidth = windowSize.width;
  if (maxHeight >= maxWidth) {
    // Taller than it is wide (or already a square), to make a square, we shrink the height to match the width
    return { width: maxWidth, height: maxWidth };
  }
  // Otherwise, it's wide (landscape mode?) - use the height as the defining dimension
  return { width: maxHeight, height: maxHeight };
}

type ImageSelectModalProps = {
  readonly isOpen: boolean;
  readonly onClose: () => void;
};

const ImageSearchImageSelect = forwardRef<
  HTMLInputElement,
  ImageSelectModalProps
>(function ImageSearchImageSelect(
  props: ImageSelectModalProps,
  inputElementRef: ForwardedRef<HTMLInputElement>,
) {
  const { onClose } = props;
  const { useStatus, setError, setLoading, setIdle } = useStatusStore<
    void,
    string
  >();

  const status = useStatus();
  const cropBox = useRef<CropBox>();
  const [viewportSize, setViewportSize] = useState<Dimensions>();
  const [imageDimensions, setImageDimensions] = useState<Dimensions>();
  const [imageUrl, setImageUrl] = useState<string>();
  const [resizedImageUrl, setResizedImageUrl] = useState<string>();
  const [fileBrowserTimeout, setFileBrowserTimeout] =
    useState<NodeJS.Timeout>();

  const handleCleanup = useCallback(
    function cleanup(): void {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
        setImageUrl(undefined);
      }
      if (resizedImageUrl) {
        URL.revokeObjectURL(resizedImageUrl);
        setResizedImageUrl(undefined);
      }
    },
    [imageUrl, setImageUrl, resizedImageUrl, setResizedImageUrl],
  );

  const handleCancel = useCallback(
    function cancel(): void {
      handleCleanup();
      onClose();
      setIdle();
    },
    [handleCleanup, setIdle, onClose],
  );

  const handleCropBoxChanged = useCallback(
    function cropBoxChanged(newCropBox: CropBox): void {
      cropBox.current = newCropBox;
    },
    [cropBox],
  );

  const handleFileBrowserCanceled = useCallback(
    function fileBrowserCanceled() {
      // If we got here, the file browser was closed and handleFileChosen() was never called.  This means
      // the user canceled out from the file browser.  End this dialog and return to the main screen.
      clearTimeout(fileBrowserTimeout);
      setFileBrowserTimeout(undefined);
      handleCancel();
    },
    [fileBrowserTimeout, setFileBrowserTimeout, handleCancel],
  );

  const handleFileBrowserClosing = useCallback(
    function fileBrowserClosing() {
      // File browser has been closed by the user.  Unfortunately, we do not know whether the user selected
      // a file or canceled the browser.  Wait a few ticks - if they chose a file, handleFlieChosen() will be called,
      // and the first thing that function does is cancel this timer.
      const timeOut = setTimeout(handleFileBrowserCanceled, cancelDelayMs);
      setFileBrowserTimeout(timeOut);
    },
    [handleFileBrowserCanceled, setFileBrowserTimeout],
  );

  const handleFileBrowserOpening = useCallback(
    function fileBrowserOpening() {
      // File browser has been opened - we need to know when it gives focus back so we can detect a 'cancel' from the user
      window.addEventListener("focus", handleFileBrowserClosing, {
        once: true,
      });
      onClose();
    },
    [handleFileBrowserClosing, onClose],
  );

  const handleImageCaptured = useCallback(
    async function onImageCaptured() {
      if (!imageUrl) {
        setError("Image is invalid. Please try again");
        return;
      }

      setLoading();
      const maxHeightWidth = 960;
      const shouldDownsizeImage =
        imageDimensions &&
        (imageDimensions?.height > maxHeightWidth ||
          imageDimensions?.width > maxHeightWidth);

      const image = !shouldDownsizeImage
        ? await getBlobFromImageURL(imageUrl)
        : await resizeImage(
            imageUrl,
            maxHeightWidth,
            maxHeightWidth,
            cropBox.current,
          );

      if (image.isError) {
        await logException(image.error);

        alert(
          `Unable to Search\nWe were unable to resize the provided image. The file may be in an invalid format.  Please choose another file and try again.`,
        );
        setError("There was an issue resizing the image");
        return;
      }

      // Create a "data url" (a url that has the entire image base64-encoded) to put in localStorage for troubleshooting
      const blob = image.value;
      let dataUrl: ArrayBuffer | string | null;

      try {
        dataUrl = await new Promise<ArrayBuffer | string | null>(
          (resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = () => {
              reject(new Error("Unable to read blob to file"));
            };
            reader.readAsDataURL(blob);
          },
        );
      } catch (error) {
        dataUrl = toError(error).message;
      }

      if (typeof dataUrl === "string") {
        localStorage.setItem("image_search_resized", dataUrl);
      }

      // Display the resized image while we wait for the AI
      setResizedImageUrl(URL.createObjectURL(image.value));

      // Uncomment these to see the resized result instead of going to search. Chrome doesn't allow top frame
      // navigation so this test code will only work for other browsers, like Firefox
      // window.location.assign(dataUrl);
      // return;

      const result = await requestImageSearch(image.value);
      if (result.isError) {
        await logException(result.error);

        alert(
          `Unable to Search\nWe ran into an issue and were unable to process your request. Please ensure you have a working internet connection, and try again. Contact us if the issue persists.`,
        );
        setError(
          "There was an issue calling the Storefront API. Please try again.",
        );
        return;
      }
      window.location.assign(`/image-search/${result.value.imageSearchId}`);
    },
    [setLoading, imageDimensions, imageUrl, setError, cropBox],
  );

  useEffect(() => {
    function handleResize() {
      setViewportSize(
        calculateViewSize(
          {
            width: window.innerWidth,
            height: window.innerHeight,
          },
          60,
        ),
      );
    }
    handleResize();
    document.getElementById("imagesUpload")?.click();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileSelected = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      clearTimeout(fileBrowserTimeout);
      setFileBrowserTimeout(undefined);

      if (!e.target.files?.[0]) {
        handleCancel();
        return;
      }
      const file = e.target.files[0];
      const url = URL.createObjectURL(file);
      if (file.type === "video/mp4") {
        handleCancel();

        alert(
          "Video not supported. Please take a photo with the device camera or choose an existing photo from the photo gallery.",
        );
        return;
      }
      e.target.value = "";
      setImageUrl(url);
      const maybeImage = await handleLoadImageFromSrc(url);
      if (maybeImage.isError) {
        setImageDimensions({ height: 960, width: 960 });
        return;
      }
      setImageDimensions({
        height: maybeImage.value.height,
        width: maybeImage.value.width,
      });
    },
    [fileBrowserTimeout, handleCancel],
  );

  const getAcceptTypes = () => {
    const acceptTypes = "image/jpeg, image/png";
    if (bowser.android && (bowser.chrome || bowser.msedge || bowser.opera)) {
      return `${acceptTypes}, application/pdf`;
    }
    return acceptTypes;
  };

  return (
    <>
      <input
        type="file"
        ref={inputElementRef}
        name="imagesUpload"
        accept={getAcceptTypes()}
        className="hidden"
        onClick={handleFileBrowserOpening}
        onChange={handleFileSelected}
        data-testid="imageSearchFileInput"
      />
      {imageUrl && (
        <dialog
          open={imageUrl !== undefined}
          className={`wss w-full h-full flex flex-col justify-center inset-0 border-0 bg-black z-1000 p-0 ${
            imageUrl !== undefined ? "fixed" : "hidden"
          }`}
        >
          <div className="absolute grid grid-row-[auto_1fr_auto] h-full left-0 top-0 w-full">
            <div className="flex flex-col p-0 row-start-1 justify-end h-[12vh]">
              {status.isLoading && (
                <p className={`${sharedTextStyles} p-4`}>
                  Searching 420,000+ products
                </p>
              )}
            </div>
            <div className="flex flex-col p-0 justify-center items-center row-start-2 w-full h-[60vh]">
              {imageUrl &&
                viewportSize &&
                imageDimensions &&
                (status.isLoading && resizedImageUrl ? (
                  <WSSImage
                    src={resizedImageUrl}
                    alt="resized"
                    width={viewportSize.width}
                    height={viewportSize.height}
                  />
                ) : (
                  <ImageSelectCropper
                    imageUrl={imageUrl}
                    imageSize={imageDimensions}
                    viewportSize={viewportSize}
                    onCropBoxChange={handleCropBoxChanged}
                  />
                ))}
            </div>
            <div className="flex flex-col p-0 justify-center row-start-3 h-[28vh]">
              {!status.isLoading && (
                <div className="flex flex-row p-0 justify-between w-full">
                  <button
                    aria-label="restart upload image"
                    type="button"
                    className={sharedButtonStyles}
                    onClick={handleCancel}
                  >
                    <p className={`${sharedTextStyles} font-thin p-4`}>
                      Cancel
                    </p>
                  </button>
                  <button
                    aria-label="upload image"
                    type="button"
                    className={sharedButtonStyles}
                    onClick={handleImageCaptured}
                  >
                    <p className={`${sharedTextStyles} font-bold p-4`}>
                      Choose
                    </p>
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="absolute top-0 left-0">
            <button
              type="button"
              aria-label="cancel upload image"
              className={`${sharedButtonStyles}`}
              onClick={handleCancel}
              id="cancelImageUploadArrow"
            >
              <Icon
                name="ri-arrow-left-line"
                theme="wss"
                className="fill-white w-[30px] h-[30px] p-4"
              />
            </button>
          </div>

          {status.isLoading && (
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 flex items-center justify-center">
              <div className="h-16 w-16 bg-white rounded-lg flex items-center justify-center">
                <Icon
                  name="ri-loader-4-fill"
                  className="animate-spin h-12 w-12 fill-green-500"
                  theme="wss"
                />
              </div>
            </div>
          )}
        </dialog>
      )}
    </>
  );
});

export default ImageSearchImageSelect;
